import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { cn } from '../../../utils/cn';

const ConversationSidebar = ({
  conversations = [],
  selectedConversation,
  onConversationSelect,
  searchQuery,
  onSearchChange,
  isOpen
}) => {
  const formatTime = (timeString) => {
    const now = new Date();
    const messageTime = new Date(timeString);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hour${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''} ago`;
    return `${Math.floor(diffInMinutes / 1440)} day${Math.floor(diffInMinutes / 1440) > 1 ? 's' : ''} ago`;
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return '';
    return message?.length > maxLength ? `${message?.substring(0, maxLength)}...` : message;
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Messages</h2>
          <div className="flex items-center space-x-2">
            <button className="p-2 hover:bg-muted rounded-lg transition-colors">
              <Icon name="MoreHorizontal" size={16} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Icon 
            name="Search" 
            size={16} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 bg-muted border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
          />
        </div>
      </div>
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {conversations?.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <Icon name="MessageCircle" size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No conversations found</p>
          </div>
        ) : (
          <div className="space-y-1 p-2">
            {conversations?.map((conversation) => (
              <button
                key={conversation?.id}
                onClick={() => onConversationSelect(conversation)}
                className={cn(
                  "w-full p-3 rounded-lg text-left hover:bg-muted/50 transition-all duration-200 relative",
                  selectedConversation?.id === conversation?.id 
                    ? "bg-primary/10 border border-primary/20" :"hover:bg-muted"
                )}
              >
                {/* Vendor Profile */}
                <div className="flex items-start space-x-3">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-muted">
                      <Image
                        src={conversation?.vendorImage}
                        alt={`${conversation?.vendorName} profile picture`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {conversation?.isOnline && (
                      <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 border-2 border-background rounded-full"></div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Vendor Name and Time */}
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-foreground text-sm truncate">
                        {conversation?.vendorName}
                      </h3>
                      <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
                        {conversation?.lastMessageTime}
                      </span>
                    </div>

                    {/* Project Title */}
                    <p className="text-xs text-primary font-medium mb-1 truncate">
                      {conversation?.projectTitle}
                    </p>

                    {/* Last Message */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-muted-foreground flex-1 truncate">
                        {truncateMessage(conversation?.lastMessage)}
                      </p>
                      
                      {/* Unread Badge */}
                      {conversation?.unreadCount > 0 && (
                        <span className="ml-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full min-w-[20px] h-5 flex items-center justify-center">
                          {conversation?.unreadCount > 99 ? '99+' : conversation?.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Actions (on hover) */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button className="p-1 hover:bg-muted rounded transition-colors">
                    <Icon name="MoreHorizontal" size={14} />
                  </button>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Quick Actions Footer */}
      <div className="p-4 border-t border-border">
        <div className="flex space-x-2">
          <button className="flex-1 flex items-center justify-center space-x-2 py-2 px-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm">
            <Icon name="Plus" size={16} />
            <span>New Chat</span>
          </button>
          <button className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="Archive" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationSidebar;